import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/feature/ComicCard';
import SearchBar from '@/components/feature/SearchBar';
import { useSEO } from '@/hooks/useSEO';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [categoryFilter, setCategoryFilter] = useState('全部');
  const [yearFilter, setYearFilter] = useState('全部');

  const years = useMemo(() => {
    const set = new Set<number>();
    comics.forEach((comic) => set.add(comic.year));
    return Array.from(set).sort((a, b) => b - a);
  }, []);

  const results = useMemo(() => {
    const lower = keyword.trim().toLowerCase();
    return comics
      .filter((comic) => {
        if (categoryFilter !== '全部' && comic.category !== categoryFilter) return false;
        if (yearFilter !== '全部' && String(comic.year) !== yearFilter) return false;
        if (!lower) return true;
        return (
          comic.title.toLowerCase().includes(lower) ||
          comic.author.toLowerCase().includes(lower) ||
          comic.description.toLowerCase().includes(lower) ||
          comic.tags.some((tag) => tag.toLowerCase().includes(lower)) ||
          comic.category.toLowerCase().includes(lower)
        );
      })
      .sort((a, b) => b.popularity - a.popularity);
  }, [keyword, categoryFilter, yearFilter]);

  const hotComics = useMemo(
    () => [...comics].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    [],
  );

  useSEO({
    title: keyword
      ? `搜索「${keyword}」- 双女主韩漫免费在线阅读`
      : '搜索双女主韩漫 - 免费在线阅读漫画',
    description: keyword
      ? `关于「${keyword}」的双女主韩漫搜索结果，共 ${results.length} 部相关作品，支持免费在线阅读。`
      : '搜索双女主韩漫作品，按关键词、题材与更新年份筛选，快速找到想看的漫画。',
    keywords: `${keyword ? `${keyword},` : ''}双女主韩漫,漫画搜索,免费阅读,在线阅读`,
  });

  const handleCategory = (value: string) => setCategoryFilter(value);
  const handleYear = (value: string) => setYearFilter(value);
  const resetAll = () => {
    setCategoryFilter('全部');
    setYearFilter('全部');
    setSearchParams({});
  };

  const hasFilter = categoryFilter !== '全部' || yearFilter !== '全部';

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-1 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
        <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">
          {keyword ? (
            <>
              搜索「<span className="text-primary-600">{keyword}</span>」的结果
            </>
          ) : (
            '搜索双女主韩漫'
          )}
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入作品名、作者或题材关键词，即可在双女主韩漫库中快速查找
        </p>
        <div className="mt-5 max-w-2xl">
          <SearchBar key={keyword} defaultValue={keyword} size="lg" autoNavigate={false} onSubmitKeyword={(value) => setSearchParams(value ? { q: value } : {})} />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-56">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-filter-3-line text-primary-500"></i>
              细化筛选
            </h2>

            <p className="mb-2 text-xs font-semibold text-foreground-500">按题材</p>
            <div className="flex flex-wrap gap-2">
              {[{ slug: '全部', name: '全部' }, ...categories].map((item) => (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => handleCategory(item.slug)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    categoryFilter === item.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            <p className="mb-2 mt-5 text-xs font-semibold text-foreground-500">按年份</p>
            <div className="flex flex-wrap gap-2">
              {['全部', ...years.map(String)].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => handleYear(item)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    yearFilter === item
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {(hasFilter || keyword) && (
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                清空搜索与筛选
              </button>
            )}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-600">
            <i className="ri-file-list-3-line text-accent-600"></i>
            共找到 <span className="font-bold text-primary-600">{results.length}</span> 部相关作品
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-3">
              {results.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <div>
                <h3 className="text-lg font-bold text-foreground-950">没有找到相关漫画</h3>
                <p className="mt-1 text-sm text-foreground-500">
                  换个关键词试试，或看看下面这些热门双女主韩漫
                </p>
              </div>
            </div>
          )}

          {results.length === 0 && (
            <div className="mt-8">
              <h2 className="mb-5 text-lg font-bold text-foreground-950">热门推荐</h2>
              <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-4">
                {hotComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}